import React, { useEffect, useRef, useState } from 'react';
import InputLocation, { InputLocationSuggestion } from '../../components/Form/InputLocation/InputLocation';
import { InputSizeTypes, InputValidationStateTypes } from '../../components/Form/Form.types';
import { debounce } from '../../assets/TypeScript/Debounce';
import { cmpLoaded, getVendorConsent } from '../../assets/TypeScript/ConsentManager';

type AutocompleteMode = 'places' | 'geocoder' | 'detecting';

type OriginalSuggestions = { mode: 'places'; data: any[] } | { mode: 'geocoder'; data: google.maps.GeocoderResult[] } | { mode: 'empty'; data: [] };

interface InputLocationWithPlacesProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
    onSearch?: (value: string) => void;
    onValueChange?: (value: string) => void;
    resetKey?: number;
    label?: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    size?: InputSizeTypes;
    validationState?: InputValidationStateTypes;
    validationMessage?: string;
    helperText?: string;
    id: string;
    showIcon?: boolean;
    showLocationButton?: boolean;
    onLocationRequest?: (lat: number, lng: number) => void;
    locationButtonLabel?: string;
    /** Geolocation failure messages. Defaults stay English so other consumers keep their behaviour. */
    locationMessages?: {
        notSupported?: string;
        error?: string;
        denied?: string;
        unavailable?: string;
        timeout?: string;
    };
}

/**
 * Checks whether the error indicates that Places API (New) is not enabled.
 */
function isPlacesApiNotEnabledError(error: unknown): boolean {
    if (!error) return false;
    const message = String((error as any)?.message || error);
    return message.includes('has not been used in project') || message.includes('is disabled') || message.includes('API_NOT_CONNECTED');
}

function buildCompatiblePlace(
    placeId: string,
    name: string,
    formattedAddress: string,
    geometry: google.maps.places.PlaceGeometry | undefined,
    types: string[]
): google.maps.places.PlaceResult {
    return { place_id: placeId, name, formatted_address: formattedAddress, geometry, types };
}

const InputLocationWithPlaces: React.FC<InputLocationWithPlacesProps> = ({
    onPlaceSelect,
    onSearch,
    onValueChange,
    resetKey,
    label,
    placeholder = 'z.B. 30161 oder Hannover',
    className = '',
    disabled = false,
    size = InputSizeTypes.md,
    validationState,
    validationMessage,
    helperText,
    id,
    showIcon = true,
    showLocationButton = false,
    onLocationRequest,
    locationButtonLabel,
    locationMessages,
}) => {
    const [inputValue, setInputValue] = useState('');
    const isFirstRender = useRef(true);

    // Reset input when resetKey changes (e.g. "Filter zurücksetzen" button)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setInputValue('');
    }, [resetKey]);

    const [suggestions, setSuggestions] = useState<InputLocationSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    // Geolocation failures used to interrupt with a blocking alert(); they now surface in the
    // input's own validation slot, which is already rendered below the field.
    const [locationError, setLocationError] = useState<string | null>(null);
    const suppressNextFetchRef = useRef(false);
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const originalSuggestionsRef = useRef<OriginalSuggestions>({ mode: 'empty', data: [] });

    // Autodetect: start as 'detecting', switch to 'places' or 'geocoder' on first API call
    const autocompleteModeRef = useRef<AutocompleteMode>('detecting');

    // --- Places API (New) autocomplete ---
    const fetchWithPlacesApi = async (value: string): Promise<InputLocationSuggestion[]> => {
        const request = {
            input: value,
            includedPrimaryTypes: ['locality', 'administrative_area_level_1', 'administrative_area_level_2', 'neighborhood', 'sublocality'],
        } as any;

        if (sessionTokenRef.current) {
            request.sessionToken = sessionTokenRef.current;
        }

        const { suggestions: fetched } = await (window.google.maps.places as any).AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (!fetched || fetched.length === 0) {
            originalSuggestionsRef.current = { mode: 'empty', data: [] };
            return [];
        }

        originalSuggestionsRef.current = { mode: 'places', data: fetched };

        return fetched.map((suggestion: any) => {
            const prediction = suggestion.placePrediction;
            const mainTextStr = typeof prediction?.mainText === 'string' ? prediction.mainText : (prediction?.mainText as any)?.text || '';
            const secondaryTextStr = typeof prediction?.secondaryText === 'string' ? prediction.secondaryText : (prediction?.secondaryText as any)?.text || '';

            return {
                place_id: prediction?.placeId || '',
                description: prediction?.description || mainTextStr,
                main_text: mainTextStr,
                secondary_text: secondaryTextStr,
            };
        });
    };

    // --- Geocoder fallback autocomplete ---
    const fetchWithGeocoder = async (value: string): Promise<InputLocationSuggestion[]> => {
        if (!geocoderRef.current) return [];

        try {
            const result = await geocoderRef.current.geocode({ address: value });
            const results = result?.results || [];

            if (results.length === 0) return [];

            originalSuggestionsRef.current = { mode: 'geocoder', data: results };

            return results.slice(0, 5).map((r) => {
                const components = r.address_components || [];
                const locality = components.find((c) => c.types.includes('locality'));
                const region = components.find((c) => c.types.includes('administrative_area_level_1'));
                const mainText = locality?.long_name || region?.long_name || r.formatted_address;
                const secondaryText = locality ? region?.long_name || '' : '';

                return {
                    place_id: r.place_id || '',
                    description: r.formatted_address,
                    main_text: mainText,
                    secondary_text: secondaryText,
                };
            });
        } catch (error) {
            console.warn('[InputLocationWithPlaces] Geocoder fallback error:', error);
            return [];
        }
    };

    // Debounced API call - fetch suggestions after short pause (100ms)
    const fetchSuggestionsDebounced = useRef(
        debounce(async (newValue: string) => {
            if (suppressNextFetchRef.current) {
                suppressNextFetchRef.current = false;
                setLoading(false);
                return;
            }
            if (newValue.length > 2) {
                try {
                    let results: InputLocationSuggestion[];
                    const mode = autocompleteModeRef.current;

                    if (mode === 'geocoder') {
                        results = await fetchWithGeocoder(newValue);
                    } else {
                        try {
                            results = await fetchWithPlacesApi(newValue);
                            if (mode === 'detecting') {
                                autocompleteModeRef.current = 'places';
                                console.info('[LocationFinder] Places API (New) available — using autocomplete.');
                            }
                        } catch (placesError) {
                            if (isPlacesApiNotEnabledError(placesError)) {
                                autocompleteModeRef.current = 'geocoder';
                                console.info('[LocationFinder] Places API (New) not available — using Geocoder fallback for address suggestions.');
                                results = await fetchWithGeocoder(newValue);
                            } else {
                                throw placesError;
                            }
                        }
                    }

                    setSuggestions(results);
                } catch (error) {
                    console.error('[InputLocationWithPlaces] Autocomplete error:', error);
                    setSuggestions([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setSuggestions([]);
                setLoading(false);
            }
        }, 100)
    ).current;

    // Initialize Google services when component becomes enabled (disabled=false means Google is loaded)
    useEffect(() => {
        cmpLoaded(() => {
            if (getVendorConsent('s135') && !disabled && !isGoogleLoaded && window.google?.maps?.places) {
                initializeServices();
            }
        });
    }, [disabled, isGoogleLoaded]);

    const initializeServices = () => {
        if (window.google?.maps?.places) {
            geocoderRef.current = new window.google.maps.Geocoder();
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
            setIsGoogleLoaded(true);
        }
    };

    const handleValueChange = (newValue: string) => {
        setInputValue(newValue);
        onValueChange?.(newValue);
        // A geolocation error must not outlive the input it complains about: it overrides the
        // consumer's validationState/validationMessage, so typing on would keep showing it.
        setLocationError(null);

        cmpLoaded(() => {
            if (!getVendorConsent('s135')) {
                return;
            }
            if (isGoogleLoaded) {
                if (newValue.length > 2) {
                    setLoading(true);
                } else {
                    setSuggestions([]);
                    setLoading(false);
                }
                fetchSuggestionsDebounced(newValue);
            } else {
                setSuggestions([]);
            }
        });
    };

    const handlePlaceSelect = async (suggestion: InputLocationSuggestion) => {
        suppressNextFetchRef.current = true;

        if (!isGoogleLoaded) {
            setSuggestions([]);
            return;
        }

        try {
            const stored = originalSuggestionsRef.current;

            if (stored.mode === 'geocoder') {
                const geocoderResult = stored.data.find((r) => r.place_id === suggestion.place_id);
                if (geocoderResult) {
                    onPlaceSelect(
                        buildCompatiblePlace(
                            geocoderResult.place_id,
                            suggestion.main_text,
                            geocoderResult.formatted_address,
                            geocoderResult.geometry,
                            geocoderResult.types || []
                        )
                    );
                }
            } else if (stored.mode === 'places') {
                const originalSuggestion = stored.data.find((s: any) => s.placePrediction?.placeId === suggestion.place_id);

                if (!originalSuggestion) {
                    console.warn('[InputLocationWithPlaces] Original suggestion not found for:', suggestion.place_id);
                    setSuggestions([]);
                    return;
                }

                const place = await originalSuggestion.placePrediction.toPlace();
                await place.fetchFields({
                    fields: ['displayName', 'formattedAddress', 'location'],
                });

                onPlaceSelect(
                    buildCompatiblePlace(
                        suggestion.place_id,
                        place.displayName || '',
                        place.formattedAddress || '',
                        (place as any).location ? { location: (place as any).location } : undefined,
                        []
                    )
                );

                sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
            }
        } catch (error) {
            console.error('[InputLocationWithPlaces] Place details error:', error);
        }

        setSuggestions([]);
        setLoading(false);
        originalSuggestionsRef.current = { mode: 'empty', data: [] };
    };

    const applyLocationResult = (text: string, lat: number, lng: number) => {
        setInputValue(text);
        onValueChange?.(text);
        onLocationRequest?.(lat, lng);
    };

    const handleLocationRequest = () => {
        setLocationError(null);

        if (!navigator.geolocation) {
            setLocationError(locationMessages?.notSupported || 'Location service is not supported by this browser.');
            return;
        }

        setLocationLoading(true);

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                if (geocoderRef.current && isGoogleLoaded) {
                    try {
                        const result = await geocoderRef.current.geocode({ location: { lat, lng } });
                        const results = result?.results || [];

                        if (results && results.length > 0) {
                            applyLocationResult(results[0].formatted_address, lat, lng);
                        } else {
                            applyLocationResult(`${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng);
                        }
                    } catch (error) {
                        console.error('[InputLocationWithPlaces] Geocode error:', error);
                        applyLocationResult(`${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng);
                    } finally {
                        setLocationLoading(false);
                    }
                } else {
                    setLocationLoading(false);
                    onLocationRequest?.(lat, lng);
                }
            },
            (error) => {
                setLocationLoading(false);
                let message = locationMessages?.error || 'Could not determine location.';

                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        message = locationMessages?.denied || 'Location access was denied.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        message = locationMessages?.unavailable || 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        message = locationMessages?.timeout || 'Location request timed out.';
                        break;
                }

                setLocationError(message);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000,
            }
        );
    };

    return (
        <InputLocation
            id={id}
            label={label}
            placeholder={placeholder}
            className={className}
            disabled={disabled}
            size={size}
            validationState={locationError ? InputValidationStateTypes.error : validationState}
            validationMessage={locationError ?? validationMessage}
            helperText={helperText}
            value={inputValue}
            suggestions={suggestions}
            loading={loading}
            showIcon={showIcon}
            showLocationButton={showLocationButton}
            onLocationRequest={handleLocationRequest}
            locationButtonLabel={locationButtonLabel}
            locationLoading={locationLoading}
            onValueChange={handleValueChange}
            onPlaceSelect={handlePlaceSelect}
            onSearch={onSearch}
        />
    );
};

export default InputLocationWithPlaces;
